import { DatePipe } from '@angular/common';
import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { forkJoin, take } from 'rxjs';
import {
  FiltersComponent, Alert, AlertsRepositoryServiceService, EnumeratedTypesRepositoryService, CorporationRepositoryService,
  SelectOption, eTrackedAlertTypeEntity, ALERT_SEVERITY_OPTIONS, ALERT_STATUS_OPTIONS, TRACKED_ITEM_OPTIONS
} from 'reg-hub-common';
import { EnvironmentUrlService } from '../../../services/environment-url/environment-url.service';

// Controls that hold a list of values, so a single deep-linked value has to be wrapped in an array
const MULTI_VALUE_CONTROLS = ['alertTypes', 'alertSeverities', 'alertStatuses'];

// The API matches Alert ID exactly and reads it as a GUID, so anything else is caught here rather than sent
const ALERT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// The detail fields each Tracked Item type shows; the rest are cleared when the type changes
const TRACKED_ITEM_FIELDS: Record<eTrackedAlertTypeEntity, string[]> = {
  [eTrackedAlertTypeEntity.SerialNumber]: ['serialNumber'],
  [eTrackedAlertTypeEntity.Individual]: ['firstName', 'middleName', 'lastName', 'dateOfBirth'],
  [eTrackedAlertTypeEntity.Business]: ['businessName', 'businessNumber']
};

// Mirrors the client portal's alerts filter, with Corporation in place of the client's Assigned To
@Component({
  selector: 'app-alerts-filter',
  templateUrl: './alerts-filter.component.html',
  styleUrl: './alerts-filter.component.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AlertsFilterComponent extends FiltersComponent<Alert> implements OnInit {
  corporations: SelectOption[] = [];
  alertTypes: SelectOption[] = [];
  alertSeverities = ALERT_SEVERITY_OPTIONS;
  alertStatuses = ALERT_STATUS_OPTIONS;
  trackedItemTypes = TRACKED_ITEM_OPTIONS;
  readonly eTrackedAlertTypeEntity = eTrackedAlertTypeEntity;

  constructor(
    repo: AlertsRepositoryServiceService,
    formBuilder: FormBuilder,
    datePipe: DatePipe,
    private route: ActivatedRoute,
    private environment: EnvironmentUrlService,
    private enumerationRepo: EnumeratedTypesRepositoryService,
    private corporationsRepo: CorporationRepositoryService
  ) {
    super(repo, formBuilder, datePipe);
  }

  ngOnInit(): void {
    forkJoin([
      this.enumerationRepo.getAlertTypesAsSelectOptions(this.environment.urlAddress),
      this.corporationsRepo.getCorporationsAsSelectOptions()
    ]).subscribe(([alertTypes, corporations]) => {
      this.alertTypes = alertTypes;
      this.corporations = corporations;
    });

    this._filterForm = this.formBuilder.group({
      corporationID: [null],
      alertTypes: [null],
      alertSeverities: [null],
      alertStatuses: [null],
      alertID: [null, Validators.pattern(ALERT_ID_PATTERN)],
      trackedAlertEntityTypeID: [null],
      serialNumber: [null],
      firstName: [null],
      middleName: [null],
      lastName: [null],
      dateOfBirth: [null],
      businessName: [null],
      businessNumber: [null]
    });

    // A hidden field mustn't keep filtering, so switching type clears the fields the new type doesn't show
    this._filterForm.get('trackedAlertEntityTypeID')!.valueChanges.subscribe((type: eTrackedAlertTypeEntity | null) => {
      const keep = type ? TRACKED_ITEM_FIELDS[type] : [];
      Object.values(TRACKED_ITEM_FIELDS).flat()
        .filter(field => !keep.includes(field))
        .forEach(field => this._filterForm.get(field)!.setValue(null, { emitEvent: false }));
    });

    this.getFirstPage();
  }

  get trackedItemType(): eTrackedAlertTypeEntity | null {
    return this._filterForm.get('trackedAlertEntityTypeID')!.value;
  }

  protected override onFilter() {
    // Pasted IDs often carry stray spaces
    const alertID = this._filterForm.get('alertID')!;
    if (typeof alertID.value === 'string') {
      alertID.setValue(alertID.value.trim() || null);
    }

    if (alertID.invalid) {
      alertID.markAsTouched();
      return;
    }

    super.onFilter();
  }

  private getFirstPage() {
    this._defaultParams = this.buildParamsFromForm(this._filterForm);

    // Allows links such as /alerts?alertID=… (an ID a client read out from their alert page)
    this.route.queryParams.pipe(take(1)).subscribe(queryParams => {
      const keys = Object.keys(queryParams).filter(key => this._filterForm.get(key));

      if (keys.length > 0) {
        keys.forEach(key => {
          const value = queryParams[key];
          this._filterForm.get(key)!.setValue(MULTI_VALUE_CONTROLS.includes(key) && !Array.isArray(value) ? [value] : value);
        });
        this._filterForm.markAsDirty();
        this.filtered = true;
        this.repo.getFirstPage(this.buildParamsFromForm(this._filterForm));
      } else {
        this.repo.getFirstPage(this._defaultParams);
      }
    });
  }
}
